import { QueryClientProvider, type QueryClient } from '@tanstack/react-query';
import { useCallback, useMemo, useState, type ReactNode } from 'react';
import { ToastProvider, TooltipProvider } from '@/design-system';
import { getDictionary, I18nContext, type Locale } from '@/lib/i18n';
import { AuthProvider } from './auth-provider';
import { QueryProvider } from './query-provider';
import { ThemeProvider } from './theme-provider';

/**
 * Ordem dos provedores (de fora para dentro) e por que ela importa:
 *
 *   ThemeProvider   → aplica `data-theme` antes de qualquer pintura
 *   I18nProvider    → o dicionário precisa existir antes de qualquer texto
 *   ToastProvider   → o QueryProvider consome `useToast` para erros globais
 *   QueryProvider   → o AuthProvider consome `useQueryClient` para limpar cache
 *   AuthProvider    → tudo abaixo depende da sessão
 *   TooltipProvider → primitivo de UI, pode ser o mais interno
 */
export function AppProviders({
  children,
  queryClient,
}: {
  readonly children: ReactNode;
  /** Injetável nos testes, para desligar retry e isolar o cache. */
  readonly queryClient?: QueryClient;
}) {
  return (
    <ThemeProvider>
      <I18nProvider>
        <ToastProvider>
          {queryClient ? (
            <QueryClientProvider client={queryClient}>
              <AuthProvider>
                <TooltipProvider>{children}</TooltipProvider>
              </AuthProvider>
            </QueryClientProvider>
          ) : (
            <QueryProvider>
              <AuthProvider>
                <TooltipProvider>{children}</TooltipProvider>
              </AuthProvider>
            </QueryProvider>
          )}
        </ToastProvider>
      </I18nProvider>
    </ThemeProvider>
  );
}

const LOCALE_COOKIE = 'qc_locale';

function readLocaleCookie(): Locale | null {
  if (typeof document === 'undefined') return null;
  const match = /(?:^|;\s*)qc_locale=(pt-BR|en-US)/.exec(document.cookie);
  return (match?.[1] as Locale | undefined) ?? null;
}

export function I18nProvider({ children }: { readonly children: ReactNode }) {
  const [locale, setLocaleState] = useState<Locale>(
    () =>
      readLocaleCookie() ?? ((import.meta.env.VITE_DEFAULT_LOCALE as Locale | undefined) ?? 'pt-BR'),
  );

  const setLocale = useCallback((next: Locale) => {
    setLocaleState(next);
    // Preferência de idioma NÃO é dado sensível: cookie comum, 1 ano.
    // (O token de sessão é outro assunto — nunca sai do cookie httpOnly.)
    document.cookie = `${LOCALE_COOKIE}=${next}; path=/; max-age=31536000; SameSite=Strict`;
    document.documentElement.lang = next;
  }, []);

  const value = useMemo(() => ({ locale, t: getDictionary(locale), setLocale }), [locale, setLocale]);

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}
